import { useEffect, useState, useRef } from "react";
import { products } from "../lib/api";
import type { Product, FetchedProduct } from "../lib/api";
import { Plus, Trash2, RefreshCw, Package, ExternalLink, Pencil, CheckCircle, AlertCircle, Loader2, DollarSign, Tag, ShoppingBag } from "lucide-react";
import clsx from "clsx";

/**
 * Format stock status for display
 * "in_stock" -> "In Stock"
 * "out_of_stock" -> "Out of Stock"
 */
function formatStockStatus(status: string): string {
  return status
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Format retailer name for display
 */
function formatRetailerName(retailer: string): string {
  const names: Record<string, string> = {
    walmart: "Walmart",
    pokemoncenter: "Pokemon Center",
    target: "Target",
  };
  return names[retailer.toLowerCase()] || retailer;
}

export default function Products() {
  const [productList, setProductList] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [url, setUrl] = useState("");
  const [editForm, setEditForm] = useState({ name: "", sku: "", price: "" });
  const [fetchedProduct, setFetchedProduct] = useState<FetchedProduct | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchData = async () => {
    try {
      const data = await products.list();
      setProductList(data);
    } catch (error) {
      console.error("Failed to fetch products", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Auto-fetch product details when URL is pasted/changed
  const fetchProductFromUrl = async (inputUrl: string) => {
    const trimmedUrl = inputUrl.trim();
    
    if (!trimmedUrl) {
      setFetchedProduct(null);
      setFetchError("");
      return;
    }

    // Validate URL format
    try {
      new URL(trimmedUrl);
    } catch {
      setFetchedProduct(null);
      setFetchError("");
      return;
    }

    // Check if it's a supported retailer URL
    const isSupported = 
      trimmedUrl.includes("walmart.com") || 
      trimmedUrl.includes("pokemoncenter.com") || 
      trimmedUrl.includes("target.com");
    
    if (!isSupported) {
      setFetchError("Unsupported URL. Supported retailers: Walmart, Pokemon Center, Target");
      return;
    }

    setFetchLoading(true);
    setFetchError("");
    
    try {
      const result = await products.fetch(trimmedUrl);
      setFetchedProduct(result);
    } catch (err) {
      console.error("Failed to fetch product", err);
      setFetchError((err as Error).message || "Failed to fetch product details");
      setFetchedProduct(null);
    } finally {
      setFetchLoading(false);
    }
  };

  // Debounced URL change handler
  const handleUrlChange = (value: string) => {
    setUrl(value);
    setFetchedProduct(null);
    setFetchError("");

    // Clear existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Set new timeout for auto-fetch (debounce 500ms)
    if (value.trim()) {
      fetchTimeoutRef.current = setTimeout(() => {
        fetchProductFromUrl(value);
      }, 500);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    // Must have fetched product data first
    if (!fetchedProduct) {
      // Try to fetch if URL is entered
      if (url.trim()) {
        await fetchProductFromUrl(url);
      } else {
        setError("Please enter a product URL first");
      }
      return;
    }
    
    if (fetchedProduct.alreadyExists) {
      setError("This product already exists in your list");
      return;
    }
    
    setSubmitting(true);
    try {
      await products.create({ 
        url: fetchedProduct.url, 
        productId: fetchedProduct.productId,
        retailer: fetchedProduct.retailer,
        name: fetchedProduct.name,
        description: fetchedProduct.description,
        price: fetchedProduct.price,
        imageUrl: fetchedProduct.imageUrl,
        status: fetchedProduct.status,
        sku: fetchedProduct.sku,
      });
      setShowCreate(false);
      resetCreateForm();
      fetchData();
    } catch (err) {
      console.error("Failed to create product", err);
      setError((err as Error).message || "Failed to create product");
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setUrl("");
    setError("");
    setFetchedProduct(null);
    setFetchError("");
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
  };

  const openEdit = (product: Product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name,
      sku: product.sku || "",
      price: product.price?.toString() || "",
    });
    setShowEdit(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;
    
    setSubmitting(true);
    try {
      await products.update(editingProduct.id, {
        name: editForm.name,
        sku: editForm.sku || undefined,
        price: editForm.price ? parseFloat(editForm.price) : undefined,
      });
      setShowEdit(false);
      setEditingProduct(null);
      fetchData();
    } catch (err) {
      console.error("Failed to update product", err);
      setError((err as Error).message || "Failed to update product");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteProduct) return;
    setDeleting(true);
    try {
      await products.delete(deleteProduct.id);
      setDeleteProduct(null);
      fetchData();
    } catch (error) {
      console.error("Failed to delete product", error);
    } finally {
      setDeleting(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Products ({productList.length})</h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          Add Product
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-lg font-semibold mb-4">Add Product</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              {error && (
                <div className="p-3 bg-error/20 border border-error/50 rounded-lg text-error text-sm">
                  {error}
                </div>
              )}
              {fetchError && (
                <div className="p-3 bg-error/20 border border-error/50 rounded-lg text-error text-sm">
                  {fetchError}
                </div>
              )}
              
              <div>
                <label className="label">Product URL</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Paste product URL..."
                  value={url}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Walmart, Pokemon Center, Target
                </p>
              </div>

              {/* Loading State */}
              {fetchLoading && (
                <div className="p-6 rounded-lg border border-dark-500 bg-dark-700/50">
                  <div className="flex items-center justify-center gap-3">
                    <Loader2 size={24} className="animate-spin text-primary-500" />
                    <span className="text-gray-400">Fetching product details...</span>
                  </div>
                </div>
              )}

              {/* Fetched Product Preview */}
              {fetchedProduct && !fetchLoading && (
                <div className={clsx(
                  "rounded-lg border overflow-hidden",
                  fetchedProduct.alreadyExists 
                    ? "border-warning/50 bg-warning/5" 
                    : "border-success/50 bg-success/5"
                )}>
                  {/* Status Banner */}
                  <div className={clsx(
                    "px-4 py-2 flex items-center gap-2",
                    fetchedProduct.alreadyExists 
                      ? "bg-warning/20 text-warning" 
                      : "bg-success/20 text-success"
                  )}>
                    {fetchedProduct.alreadyExists ? (
                      <>
                        <AlertCircle size={16} />
                        <span className="text-sm font-medium">Product Already Exists</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        <span className="text-sm font-medium">Product Found</span>
                      </>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="p-4">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      {fetchedProduct.imageUrl ? (
                        <img
                          src={fetchedProduct.imageUrl}
                          alt={fetchedProduct.name}
                          className="w-32 h-32 rounded-lg object-cover bg-dark-600 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-dark-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package size={40} className="text-gray-500" />
                        </div>
                      )}

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-lg leading-tight line-clamp-2">
                          {fetchedProduct.name}
                        </h4>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          {/* Retailer Badge */}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-dark-600 text-xs font-medium">
                            <ShoppingBag size={12} />
                            {formatRetailerName(fetchedProduct.retailer)}
                          </span>
                          
                          {/* Price */}
                          {fetchedProduct.price && (
                            <span className="inline-flex items-center gap-1 text-primary-500 font-semibold">
                              <DollarSign size={14} />
                              {fetchedProduct.price.toFixed(2)}
                            </span>
                          )}
                          
                          {/* Stock Status */}
                          <span className={clsx(
                            "status-badge",
                            fetchedProduct.status === "in_stock" && "bg-success/20 text-success",
                            fetchedProduct.status === "out_of_stock" && "bg-error/20 text-error",
                            fetchedProduct.status === "preorder" && "bg-warning/20 text-warning",
                            fetchedProduct.status === "unknown" && "bg-gray-500/20 text-gray-400"
                          )}>
                            {formatStockStatus(fetchedProduct.status)}
                          </span>
                        </div>

                        {/* Description */}
                        {fetchedProduct.description && (
                          <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                            {fetchedProduct.description}
                          </p>
                        )}

                        {/* Additional Info */}
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                          {fetchedProduct.sku && (
                            <span className="flex items-center gap-1">
                              <Tag size={12} />
                              SKU: {fetchedProduct.sku}
                            </span>
                          )}
                          {fetchedProduct.brand && (
                            <span>Brand: {fetchedProduct.brand}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* URL */}
                    <div className="mt-3 pt-3 border-t border-dark-500">
                      <a 
                        href={fetchedProduct.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary-500 hover:underline flex items-center gap-1 truncate"
                      >
                        <ExternalLink size={12} />
                        {fetchedProduct.url}
                      </a>
                    </div>

                    {/* Already Exists Warning */}
                    {fetchedProduct.alreadyExists && fetchedProduct.existingProduct && (
                      <div className="mt-3 pt-3 border-t border-dark-500 text-sm text-warning">
                        Already saved as: {fetchedProduct.existingProduct.name}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <button 
                  type="button" 
                  onClick={() => { setShowCreate(false); resetCreateForm(); }} 
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={submitting || fetchLoading || !fetchedProduct || fetchedProduct.alreadyExists}
                >
                  {submitting ? "Adding..." : "Add Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-lg w-full">
            <h3 className="font-display text-lg font-semibold mb-4">Edit Product</h3>
            <form onSubmit={handleEdit} className="space-y-4">
              {error && (
                <div className="p-3 bg-error/20 border border-error/50 rounded-lg text-error text-sm">
                  {error}
                </div>
              )}
              <div>
                <label className="label">Product Name</label>
                <input
                  type="text"
                  className="input"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">SKU (optional)</label>
                <input
                  type="text"
                  className="input"
                  value={editForm.sku}
                  onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Price (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input"
                  placeholder="0.00"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                />
              </div>
              <div className="text-sm text-gray-500">
                <p>Retailer: {formatRetailerName(editingProduct.retailer)}</p>
                <p>Product ID: {editingProduct.productId}</p>
              </div>
              <div className="flex gap-3 justify-end">
                <button 
                  type="button" 
                  onClick={() => { setShowEdit(false); setError(""); }} 
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full">
            <h3 className="font-display text-lg font-semibold mb-2">Delete Product</h3>
            <p className="text-gray-400 mb-4">
              Are you sure you want to delete <span className="text-white font-medium">"{deleteProduct.name}"</span>?
            </p>
            <p className="text-sm text-warning mb-6">
              This will also delete any tasks associated with this product.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteProduct(null)}
                className="btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="btn-danger"
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product List */}
      {productList.length === 0 ? (
        <div className="card text-center py-12">
          <Package size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No products yet. Add a product URL to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {productList.map((product) => (
            <div key={product.id} className="card">
              <div className="flex gap-4">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-dark-600 rounded-lg flex items-center justify-center">
                    <Package size={32} className="text-gray-500" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold line-clamp-2">{product.name}</h4>
                  <p className="text-sm text-gray-400 mt-1">{formatRetailerName(product.retailer)}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span
                      className={clsx(
                        "status-badge",
                        product.status === "in_stock" && "bg-success/20 text-success",
                        product.status === "out_of_stock" && "bg-error/20 text-error",
                        product.status === "preorder" && "bg-warning/20 text-warning",
                        product.status === "unknown" && "bg-gray-500/20 text-gray-400"
                      )}
                    >
                      {formatStockStatus(product.status)}
                    </span>
                    {product.price && (
                      <span className="text-primary-500 font-semibold">${product.price.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-600">
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-400 hover:text-primary-500 flex items-center gap-1"
                >
                  <ExternalLink size={14} />
                  View on {formatRetailerName(product.retailer)}
                </a>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEdit(product)}
                    className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil size={18} className="text-gray-400 hover:text-primary-500" />
                  </button>
                  <button
                    onClick={() => setDeleteProduct(product)}
                    className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} className="text-error" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
